/**
 * Firebase Auth JWT custom-claims reader for the web admin surface.
 *
 * Mirrors the iOS `AuthClaims` struct (OMON-114) and the Python
 * `JWTClaim` dataclass in agent.fhir/practitioner_role.py. The shape
 * is defined by ADR-0013 §5 and projected from `_practitionerRole`
 * docs by agent.fhir/practitioner_role.py::project_to_jwt_claim().
 *
 * Fail-closed: any malformed input returns null. A null claim means
 * the user has no usable role — admin UI items don't render, write
 * paths refuse, Firestore rules reject. Callers must treat null as
 * "no access" rather than fallback-to-defaults.
 */
import { getIdTokenResult, type User } from "firebase/auth";

export type AccessRole =
  | "carer"
  | "planner"
  | "librarian"
  | "admin"
  | "auditor"
  /** ADR-0013 amendment (2026-09-16): plan-building AND catalogue
   *  authoring in one grant — NON-PRODUCTION environments only. */
  | "practice-author";

export type DataScope = "real" | "test" | "both";

export interface AuthClaims {
  role: AccessRole;
  dataScope: DataScope;
  personDocId: string;
  careTeamMemberships: string[];
}

const VALID_ROLES: ReadonlySet<string> = new Set([
  "carer",
  "planner",
  "librarian",
  "admin",
  "auditor",
  "practice-author",
]);

const VALID_DATA_SCOPES: ReadonlySet<string> = new Set([
  "real",
  "test",
  "both",
]);

/**
 * Parse a raw claims map into an `AuthClaims`, or null on any
 * deviation from the expected shape.
 *
 * Pure function — exported separately from `loadClaims()` so tests can
 * exercise the parsing logic without a Firebase Auth instance. The two
 * fields `role` and `dataScope` are checked against the closed sets
 * from ADR-0013; an unrecognised string fails the whole projection.
 */
/**
 * OMON-250 access-map: true iff the raw token claims carry
 * `access: { plans: "all", ... }`.
 *
 * Mirrors the strict rules' `accessPlansAll()` helper, including its
 * structural guards — a missing/malformed `access` (or the future OMON-234
 * per-org list scope, e.g. `plans: ["org-1"]`) is simply `false`, never a
 * throw. Pure so tests can cover the shapes without Firebase Auth.
 *
 * Interim (OMON-234 pending): the claims projection stamps every planner
 * with `plans: "all"`, so today this returns true for all planners whose
 * token has refreshed since the access-map rollout.
 */
export function accessPlansAll(rawClaims: unknown): boolean {
  if (rawClaims === null || typeof rawClaims !== "object") return false;
  const access = (rawClaims as Record<string, unknown>).access;
  if (access === null || typeof access !== "object" || Array.isArray(access)) return false;
  return (access as Record<string, unknown>).plans === "all";
}

export function parseClaims(raw: unknown): AuthClaims | null {
  if (raw === null || typeof raw !== "object") return null;
  const claims = raw as Record<string, unknown>;

  const role = claims.role;
  if (typeof role !== "string" || !VALID_ROLES.has(role)) return null;

  const dataScope = claims.dataScope;
  if (typeof dataScope !== "string" || !VALID_DATA_SCOPES.has(dataScope)) {
    return null;
  }

  const personDocId = claims.personDocId;
  if (typeof personDocId !== "string" || personDocId.length === 0) return null;

  const careTeamMembershipsRaw = claims.careTeamMemberships;
  let careTeamMemberships: string[] = [];
  if (Array.isArray(careTeamMembershipsRaw)) {
    // Reject the whole claim if any element is non-string — fail-closed
    // beats silent-coercion when the input is malformed.
    for (const entry of careTeamMembershipsRaw) {
      if (typeof entry !== "string" || entry.length === 0) return null;
      careTeamMemberships.push(entry);
    }
  } else if (careTeamMembershipsRaw !== undefined) {
    // Field present but not an array — malformed.
    return null;
  }

  return {
    role: role as AccessRole,
    dataScope: dataScope as DataScope,
    personDocId,
    careTeamMemberships,
  };
}

/**
 * Force-refresh the user's ID token and return the parsed claims.
 *
 * `forceRefresh=true` ensures we pick up claims that were written by
 * the admin tooling after the user signed in (per the caller-driven
 * projection architecture — see agent.fhir/claims_projection.py).
 * Without this the SDK happily returns an hour-old token whose
 * claims reflect the pre-projection state.
 */
export async function loadClaims(user: User): Promise<AuthClaims | null> {
  try {
    const result = await getIdTokenResult(user, true);
    return parseClaims(result.claims);
  } catch {
    return null;
  }
}

/**
 * Predicate helpers — used by the admin shell to decide which
 * menu items to render. Keeping them centralised here means the
 * permission table from ADR-0013 §5 has exactly one definition site.
 */
export function canSeeAdminMenu(claims: AuthClaims | null): boolean {
  if (claims === null) return false;
  return claims.role === "admin" || claims.role === "auditor";
}

export function canSeeUsersAndRoles(claims: AuthClaims | null): boolean {
  return claims?.role === "admin";
}

export function canSeeAccessRequests(claims: AuthClaims | null): boolean {
  // Admin-only: actioning a request is an access-granting decision, and the
  // queue carries an unknown caller's unverified claims about a client.
  return claims?.role === "admin";
}

/**
 * Who may READ `_relatedPersonRequest` at all — the office roles, exactly as
 * the strict Firestore rules admit them (`allow read: if isAdmin() ||
 * isPlanner() || isAuditor()`). Wider than canSeeAccessRequests (the admin
 * queue): a planner's client card shows "asked to join" on a contact.
 *
 * ☠️ Why the app checks before asking: the card's read is best-effort, so a
 * refused read shows nothing and says nothing — and on the 2026-10-30 strict
 * rehearsal every one of the suite's carer-on-planner-web cells issued it and
 * was refused, which is a DENY the cutover watch cannot tell from a real one.
 * A role that the rules will refuse does not ask.
 */
export function canReadAccessRequests(claims: AuthClaims | null): boolean {
  const role = claims?.role;
  return role === "admin" || role === "planner" || role === "auditor";
}

/**
 * Soma planner adoption (Ed, 2026-09-22): which clients — and so which
 * planners — haven't got going. Admin-only: it ranks named planners, which
 * is a conversation for the founders, not a board the planners see. The
 * API enforces the same role (omon-api /v1/soma_adoption, require_admin).
 */
export function canSeeSomaAdoption(claims: AuthClaims | null): boolean {
  return claims?.role === "admin";
}

export function canSeeAuditLog(claims: AuthClaims | null): boolean {
  if (claims === null) return false;
  return claims.role === "admin" || claims.role === "auditor";
}

/**
 * BOFF-029 / connect-migration slice 1: visibility of the Connect panel
 * (the IoT coordinator overview folded into OMON from the standalone
 * app.coordinator). Admin-only for now — a deliberate UX gate while the
 * surface is built out. NOTE: this is a UX lock, not a security boundary,
 * while prod runs the permissive Firestore rules (OMON-206); the sensor
 * data is still readable by any authed user at the data layer until the
 * strict rules can be deployed. Widen to a scoped coordinator role once
 * that role + its claims projection land.
 */
export function canSeeConnect(claims: AuthClaims | null): boolean {
  // OMON-234: planners (not just admins) can reach Connect — if you can plan
  // for a client you must be able to see them in the IoT view. Data path is
  // the same scope-aware loadPatients(); this is the visibility gate only.
  return claims?.role === "admin" || claims?.role === "planner";
}

/**
 * OMON-027: Top-level nav tab visibility per ADR-0013 §3 permission table.
 *
 * | Role      | SOMA Plans | Goals Inventory |
 * |-----------|------------|------------------|
 * | planner   | ✅         | ❌               |
 * | librarian | ❌         | ✅               |
 * | admin     | ✅         | ✅               |
 * | auditor   | ✅ (RO)    | ✅ (RO)          |
 * | carer     | ❌ (iOS only) | ❌            |
 *
 * Read-only enforcement for auditor lives in Firestore Security Rules
 * (OMON-016), not in the tab predicate — auditor SEES the tabs;
 * write attempts are blocked at the data layer per defence in depth.
 *
 * Nil claims fail-closed: no tabs visible. Caller renders the empty
 * state (`defaultPanelFor()` returns null). Matches the pre-claims
 * deployment story: an authenticated user without `_practitionerRole`
 * has no role and therefore no surface — they're sent through onboarding.
 */
export function canSeeSOMAPlansTab(claims: AuthClaims | null): boolean {
  if (claims === null) return false;
  return (
    claims.role === "planner" ||
    claims.role === "admin" ||
    claims.role === "auditor" ||
    claims.role === "practice-author"
  );
}

export function canSeeGoalsInventoryTab(claims: AuthClaims | null): boolean {
  if (claims === null) return false;
  return (
    claims.role === "librarian" ||
    claims.role === "admin" ||
    claims.role === "auditor" ||
    claims.role === "practice-author"
  );
}

/**
 * The panel that should be active on sign-in for the given claims.
 *
 * Returns the first tab the user can see. If the user can see no
 * tabs (carer signing in to web, or unrecognised role / nil claims),
 * returns null — caller renders the no-access empty state.
 */
export function defaultPanelFor(
  claims: AuthClaims | null,
): "clients" | "catalogue" | null {
  if (canSeeSOMAPlansTab(claims)) return "clients";
  if (canSeeGoalsInventoryTab(claims)) return "catalogue";
  return null;
}

/**
 * OMON-027: TEST MODE indicator predicate — mirrors iOS
 * `AuthClaims.showsTestModeIndicator`. Used by the web app to render an
 * orange "TEST MODE — fictional data" strip above the nav bar.
 *
 * `dataScope: both` (Ed in dev) intentionally hides the indicator: the
 * choice of data context is made by which build/origin he opens, not
 * by chrome on top.
 */
export function showsTestModeIndicator(claims: AuthClaims | null): boolean {
  return claims?.dataScope === "test";
}
